import isEmpty from 'lodash/isEmpty'
import { v4 as uuid } from 'uuid'
import {
  getIsChildPricingEligible,
  getIsInfantPricingEligible,
} from '../../../common/multiPaxPricingEligibility'
import {
  toShopSearchRedirect,
  toCartCheckout,
} from '../../../details-client/CrossSellModalAd/crossSellHelpers'
import { buildQueryString, getSelectedFareBrands } from '../../helpers/helpers'
import { buildCheckoutObject } from '../buildCheckoutObject'
import { buildShopSearchRedirectObject } from '../buildShopSearchRedirectObject'
import { getPricingByPaxType } from '../getPricingPerPaxType/getPricingPerPaxType'

export function handleDetailsCheckout({
  airPriceResponse,
  defaultBrandsByKeys,
  isExpressDeal = false,
  isPaidBaggageEligible,
  isReshopExperience,
  isSeatEligible,
  itemKey,
  itinerarySlices,
  offerToken,
  packageSpecificPriceNodes,
  passengers,
  price,
  priceKey,
  props,
  selectionsBySliceId,
  skipSeatMap,
  // When true, skip standalone fly/seats paid-bags step (bags on details). FLIGHTS-3570.
  shouldSkipBagsCall,
  totalSeatsCost,
  selectedSeats,
}) {
  const { params, fromPackages, slicesUrl, tripType, searchRequest, features, detailsItinerary } = props

  const {
    isInterline,
    isFused,
    airline: airlines,
    airport: airports,
    equipment,
    isBEC,
  } = !isEmpty(detailsItinerary) ? detailsItinerary : airPriceResponse

  let fareBrandsInfo = airPriceResponse?.fareBrandsInfo || {}

  const { upsellOptions } = getSelectedFareBrands({
    defaultBrandsByKeys,
    selectionsBySliceId,
    fareBrandsInfo,
  })

  const isChildPricingVariant = getIsChildPricingEligible({ pclnBootstrapData: window?.PCLN_BOOTSTRAP_DATA })
  const isInfantPricingVariant = getIsInfantPricingEligible({
    pclnBootstrapData: window?.PCLN_BOOTSTRAP_DATA,
  })
  const pricingPerPaxType =
    isChildPricingVariant || isInfantPricingVariant
      ? getPricingByPaxType({
          passengers,
          upsellOptions,
          selectionsBySliceId,
          priceKey,
          price,
        })
      : {}

  sessionStorage.setItem(
    'paxTypePricing',
    JSON.stringify({
      isChildPricingVariant,
      isInfantPricingVariant,
    })
  )

  const uspCacheKey = searchRequest.params['usp-cache-key']
  const isPackagesRedirect = fromPackages && uspCacheKey

  // THIS IS FOR PACKAGES
  const tsRedirectObj = buildShopSearchRedirectObject({
    proposalComponents: [],
    itemKey,
    priceKey,
    searchRequest,
    selectedSeats,
    price,
  })

  let checkoutObj = buildCheckoutObject({
    airlines,
    airports,
    defaultBrandsByKeys,
    equipment,
    fareBrandsInfo,
    fromPackages,
    isBEC,
    isExpressDeal,
    isFused,
    isInterline,
    itemKey,
    itinerarySlices,
    offerToken,
    packageSpecificPriceNodes,
    passengers,
    price,
    priceKey,
    searchRequest,
    selectionsBySliceId,
    tripType,
    pricingPerPaxType,
  })

  const fireflyAncillaries = {}
  if (selectedSeats) {
    fireflyAncillaries.seats = selectedSeats
  }

  fireflyAncillaries.bags = updatePaidBags()

  if (isPackagesRedirect) {
    checkoutObj = {
      ...checkoutObj,
      ...{
        tsRedirectObj,
      },
    }
  }

  let seatsEligiblility = false

  // Seats Eligibility
  // TODO: with embedded seats, this fn is called after seats are selected on seatmap,
  // not before seatmap is shown. Should we move this logic before seatmap is shown?
  if (isSeatEligible) {
    seatsEligiblility = isSeatMapEligible({
      fromPackages,
      seatsEligiblility,
      isFused,
      features,
      tripType,
    })
  }

  let paidBagsEligiblility = false
  const skipBagsCall = shouldSkipBagsCall
  // Paid Baggage Eligibility
  if (isPaidBaggageEligible && !skipBagsCall) {
    paidBagsEligiblility = getPaidBagsAvailability({
      fromPackages,
      isFused,
      features,
      tripType,
    })
  }

  // TODO: update tsRedirectObj later with potential new paid baggage payload
  // for packages we have to go to the new middleware
  if (isPackagesRedirect) {
    toShopSearchRedirect(tsRedirectObj)
    return
  }

  /**
   * Redirect users to seats instead of Trip Summary if following conditions are met
   */
  if (!isReshopExperience && (seatsEligiblility || paidBagsEligiblility)) {
    const checkoutObjKey = uuid()
    /**
     * Store the Checkout Object in the SessionStorage for seats to make changes
     * */
    sessionStorage.setItem(checkoutObjKey, JSON.stringify(checkoutObj))

    redirectToSeats({
      checkoutObjKey,
      slicesUrl,
      params,
      priceKey,
      itemKey,
      tripType,
      fromPackages,
      seatsEligiblility,
      paidBagsEligiblility,
      checkoutObj,
      totalSeatsCost,
      selectedSeats,
      skipSeatMap,
    })
    return
  }

  toCartCheckout(checkoutObj)
}

export function getPaidBagsAvailability({ fromPackages, isFused, features = {}, tripType }) {
  const { flights = {}, whiteLabel = {} } = features

  const {
    activateFireflyBaggageSplitsPkgs,
    activateFireflyBaggagePkgs,
    activateFireflyBaggageSplits,
    activateFireflyBaggage,
    activateFireflyBaggageMD,
  } = flights

  const { flightBagsEnabled: flightBagsEnabledForWhiteLabel = true, isPricelineOnly = true } = whiteLabel

  // White Label traffic relies on the White Label toggle
  if (!isPricelineOnly) {
    return flightBagsEnabledForWhiteLabel
  }

  //  Toggle for Multi Destination
  if (tripType === 'MD') {
    return activateFireflyBaggageMD
  }

  if (fromPackages) {
    return isFused ? activateFireflyBaggageSplitsPkgs : activateFireflyBaggagePkgs
  }
  return isFused ? activateFireflyBaggageSplits : activateFireflyBaggage
}
export function isSeatMapEligible({ fromPackages, isFused, features = {}, tripType }) {
  const { flights = {}, whiteLabel = {} } = features

  const {
    activateFireflySeatsSplitsPkgs,
    activateFireflySeatsPkgs,
    activateFireflySeatsSplits,
    activateFireflySeats,
    activateFireflySeatsMD,
  } = flights

  const { flightSeatsEnabled: flightSeatsEnabledForWhiteLabel = true, isPricelineOnly = true } = whiteLabel

  // White Label traffic relies on the White Label toggle
  if (!isPricelineOnly) {
    return flightSeatsEnabledForWhiteLabel
  }

  //  Toggle for Multi Destination
  if (tripType === 'MD') {
    return activateFireflySeatsMD
  }

  if (fromPackages) {
    return isFused ? activateFireflySeatsSplitsPkgs : activateFireflySeatsPkgs
  }
  return isFused ? activateFireflySeatsSplits : activateFireflySeats
}

function redirectToSeats({
  checkoutObjKey,
  slicesUrl,
  params,
  priceKey,
  itemKey,
  tripType,
  fromPackages,
  seatsEligiblility,
  paidBagsEligiblility,
  checkoutObj,
  totalSeatsCost,
  selectedSeats,
  skipSeatMap,
}) {
  // is queryString needed below?
  const queryString = buildQueryString({
    params,
    priceKey,
    itemKey,
    itineraryType: tripType,
    fromPackages,
    checkoutObjKey,
    seatsEligiblility,
    paidBagsEligiblility,
    workflowId: window?.PCLN_BOOTSTRAP_DATA?.workflowId,
  })

  if (skipSeatMap) {
    toCartCheckoutWithSeats(checkoutObj, totalSeatsCost, checkoutObjKey, selectedSeats)
  } else {
    const seatsUrl = `/fly/seats${slicesUrl}?${queryString}`
    window.location.assign(seatsUrl)
  }
}

// SOON TO BE DEPRECATED, PING CONNOR MALONEY ABOUT USAGE
export const updatePaidBags = () => {
  const baggageData = JSON.parse(sessionStorage.getItem('bags'))
  let bags = []
  baggageData?.forEach((slice) => {
    const ancillaryToken = slice?.ancillaryToken
    const originAirportCode = slice?.originAirportCode
    const destinationAirportCode = slice?.destinationAirportCode

    slice?.passengers?.forEach((passenger) => {
      passenger?.baggageSelections?.forEach((selection) => {
        const { quantity, type, subType, amount, baggageId } = selection
        if (quantity > 0) {
          const matchedQuantity = selection?.priceByQuantity?.find(
            (priceByQuantityItem) => priceByQuantityItem.quantity === quantity
          )
          bags.push({
            quantity,
            type,
            subType,
            price: {
              amount,
              baseFare: matchedQuantity?.price?.baseFare,
              totalBaseTaxesAndFees: matchedQuantity?.price?.totalBaseTaxesAndFees,
              totalTaxes: matchedQuantity?.price?.totalTaxes,
              currencyCode: matchedQuantity?.price?.currencyCode,
              reservationFee: matchedQuantity?.price?.fees?.[0]?.amount,
            },
            baggageId,
            ancillaryToken,
            originAirportCode,
            destinationAirportCode,
            sliceRefId: slice.sliceRefId,
            passengerId: passenger.passengerId,
          })
        }
      })
    })
  })

  return bags
}

function toCartCheckoutWithSeats(checkoutObj, totalSeatsCost, checkoutObjKey, selectedSeats) {
  const cartCheckoutObj = structuredClone(checkoutObj) || {} //window?.PCLN_BOOTSTRAP_DATA?.cartCheckoutObj || {}

  if (totalSeatsCost?.totalRoundedCost) {
    cartCheckoutObj.totalSeatsCost = totalSeatsCost
  }

  const fireflyAncillaries = {}
  if (selectedSeats) {
    fireflyAncillaries.seats = selectedSeats
  }

  fireflyAncillaries.bags = updatePaidBags()

  cartCheckoutObj.components[0].meta.fireflyAncillaries = fireflyAncillaries

  // Update checkout object itmes
  sessionStorage.setItem(checkoutObjKey, cartCheckoutObj)

  const host = window.location.origin
  const checkoutPath = '/shop/search/itinerary'
  const form = document.createElement('form')
  form.setAttribute('method', 'POST')
  form.setAttribute('action', `${host}${checkoutPath}`)
  for (const prop in cartCheckoutObj) {
    const input = document.createElement('input')
    input.name = prop
    input.type = 'hidden'
    input.value =
      typeof cartCheckoutObj[prop] === 'object'
        ? JSON.stringify(cartCheckoutObj[prop])
        : cartCheckoutObj[prop]
    form.appendChild(input)
  }
  document.body.appendChild(form)
  form.submit()
}
